import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';

/**
 * Add a footer link to a specific column
 * @param {string} footerPath - Path to footer data in store
 * @param {number} column - Column number (1 or 2)
 * @returns {void}
 */
export function addFooterLinkInColumn(footerPath, column) {
	// For Focus Features, add to customLinks
	const linksPath = `${footerPath}/customLinks`;
	const customLinksObj = jsonPointer.get(Store.get().data, linksPath) || {};

	// Get global links from Navigation & Legal
	const navigationData = Store.get().data?.navigation?.content;
	const globalFooterLinksRaw = navigationData?.footer?.links || [];
	const globalFooterLinks = {};
	let linkId = 0;

	globalFooterLinksRaw.forEach((col, columnIndex) => {
		if (Array.isArray(col)) {
			col.forEach((link, indexInColumn) => {
				globalFooterLinks[linkId] = {
					id: linkId,
					name: link.name,
					destination: link.destination,
					order: indexInColumn, // Use index within column, not global linkId
					column: columnIndex + 1,
				};
				linkId++;
			});
		}
	});

	// Get ALL links (global + custom) in the target column to find max order
	const globalLinksInColumn = Object.values(globalFooterLinks).filter(link => link.column === column);
	const customLinksInColumn = Object.values(customLinksObj).filter(link => link.column === column);
	const allLinksInColumn = [...globalLinksInColumn, ...customLinksInColumn];

	// Find the highest order value in this column and add 1
	const maxOrder = allLinksInColumn.length > 0
		? Math.max(...allLinksInColumn.map(link => link.order || 0)) + 1
		: 0;

	const newId = Object.keys(customLinksObj).length;

	// Add the new link at the end
	jsonPointer.setDeep(Store.get().data, `${linksPath}/${newId}`, {
		id: newId,
		name: 'New Link',
		destination: '',
		external: true,
		order: maxOrder,
		column: column,
	});
}

/**
 * Handle footer links sort for a specific column
 * Treats each column as an independent sortable list
 * For Focus Features: only custom links can be reordered (global links are fixed from Navigation & Legal)
 * @param {string} footerPath - Path to footer data in store
 * @param {number} column - Column number (1 or 2)
 * @param {Event} e - Sort event with from/to indices
 * @returns {void}
 */
export function sortFooterLinksInColumn(footerPath, column, e) {
	// Get IDs from DOM in the new order after drag (only this column's items)
	const columnItems = [...e.target.querySelectorAll('[data-link]')].map((el) => el.dataset.link);

	// Reorder using splice
	columnItems.splice(e.to, 0, columnItems.splice(e.from, 1)[0]);

	// Get custom links only (global links cannot be reordered)
	// const customLinksObj = jsonPointer.get(Store.get().data, `${footerPath}/customLinks`) || {};

	// Get global links count in this column to determine starting order for custom links
	const navigationData = Store.get().data?.navigation?.content;
	const globalFooterLinksRaw = navigationData?.footer?.links || [];
	let globalLinksCount = 0;
	if (column === 1 && Array.isArray(globalFooterLinksRaw[0])) {
		globalLinksCount = globalFooterLinksRaw[0].length;
	} else if (column === 2 && Array.isArray(globalFooterLinksRaw[1])) {
		globalLinksCount = globalFooterLinksRaw[1].length;
	}

	// Filter to only custom links in the reordered list
	const customLinkIds = columnItems.filter(id => String(id).startsWith('custom_'));

	// Assign new order values to custom links starting after global links
	customLinkIds.forEach((linkId, index) => {
		const customId = String(linkId).replace('custom_', '');
		// Order starts after global links: globalLinksCount, globalLinksCount + 1, etc.
		jsonPointer.set(Store.get().data, `${footerPath}/customLinks/${customId}/order`, globalLinksCount + index);
	});
}
